// src/views/FastBooking/FastBooking.jsx
// HorizonUI | Chakra UI v3 | Dark/Light %100 | Responsive | Work-Logic Birebir Korundu

import React, { useState, useEffect, useRef } from "react"
import {
    Box,
    Flex,
    Text,
    Button,
    Input,
    InputGroup,
    Textarea,
    Avatar,
    AvatarFallback,
    AvatarImage,
    Badge,
    Spinner,
    Alert,
    AlertDescription,
    AlertRoot,
    AlertTitle,
    Progress,
    SimpleGrid,
    VStack,
    HStack,
    Separator,
    Skeleton,
    SkeletonCircle,
    IconButton,
    Circle,
  //  theme,
    defaultSystem,
    ChakraProvider,
} from "@chakra-ui/react"
import { useColorModeValue } from "@chakra-ui/color-mode"
import { Toaster, toaster } from "react-hot-toast";
import Calendar from "react-calendar"
import "react-calendar/dist/Calendar.css"
import { usersAPI, workScheduleAPI, appointmentsAPI, api } from "services/api"
import { useAuth } from "context/AuthContext"
import {
    FASTBOOKING_TEXT_TOP,
    FASTBOOKING_TEXT_TOP_UNDER,
    FASTBOOKING_TEXT_GENERAL_1,
    FASTBOOKING_FOOTER,
} from "config"

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS — birebir korundu
// ─────────────────────────────────────────────────────────────────────────────
const getInitials = (name) => {
    if (!name) return "?"
    const parts = name.split(" ")
    if (parts.length === 1 && parts[0].length > 1) return parts[0].substring(0, 2).toUpperCase()
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase()
}

const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
}

const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0
    const [hours, minutes] = timeStr.split(":").map(Number)
    return hours * 60 + minutes
}

const transformWorkDaysToSchedule = (workDays) => {
    const schedule = {}
    workDays.forEach((day) => {
        if (day.is_active) schedule[day.day_of_week] = { start: day.start_time, end: day.end_time }
    })
    return schedule
}

// ─────────────────────────────────────────────────────────────────────────────
// GLOBAL STYLES — calendar overrides + animations
// ─────────────────────────────────────────────────────────────────────────────
const GlobalStyles = ({ isDark }) => (
    <style>{`
    @import url("https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap");

    .fb-root * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
    .fb-elegant { font-family: 'DM Serif Display', serif !important; }

    .fb-calendar .react-calendar {
      background: ${isDark ? "rgba(255,255,255,0.04)" : "#ffffff"} !important;
      border: 1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"} !important;
      border-radius: 16px !important;
      color: ${isDark ? "#e2e8f0" : "#1a202c"} !important;
      width: 100% !important;
      padding: 12px !important;
      font-family: 'DM Sans', sans-serif !important;
    }
    .fb-calendar .react-calendar__navigation button {
      color: ${isDark ? "#90cdf4" : "#3182ce"} !important;
      font-weight: 700 !important;
      font-size: 0.85rem !important;
      text-transform: uppercase !important;
      border-radius: 10px !important;
      min-width: 36px !important;
    }
    .fb-calendar .react-calendar__navigation button:hover {
      background: ${isDark ? "rgba(144,205,244,0.1)" : "rgba(49,130,206,0.08)"} !important;
    }
    .fb-calendar .react-calendar__month-view__weekdays__weekday {
      color: ${isDark ? "#718096" : "#a0aec0"} !important;
      font-weight: 600 !important;
      font-size: 0.72rem !important;
      text-transform: uppercase !important;
    }
    .fb-calendar .react-calendar__month-view__weekdays__weekday abbr { text-decoration: none !important; }
    .fb-calendar .react-calendar__tile {
      color: ${isDark ? "#e2e8f0" : "#1a202c"} !important;
      border-radius: 10px !important;
      height: 40px !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 0.85rem !important;
      font-weight: 500 !important;
      transition: all 0.18s !important;
    }
    .fb-calendar .react-calendar__tile:enabled:hover {
      background: ${isDark ? "rgba(144,205,244,0.12)" : "rgba(49,130,206,0.08)"} !important;
      color: ${isDark ? "#90cdf4" : "#3182ce"} !important;
    }
    .fb-calendar .react-calendar__tile--active,
    .fb-calendar .react-calendar__tile--active:enabled:hover {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
      color: #fff !important;
      font-weight: 700 !important;
      box-shadow: 0 4px 12px rgba(102,126,234,0.4) !important;
    }
    .fb-calendar .react-calendar__tile--now {
      background: ${isDark ? "rgba(144,205,244,0.08)" : "rgba(49,130,206,0.06)"} !important;
      border: 1.5px solid ${isDark ? "#90cdf4" : "#3182ce"} !important;
      color: ${isDark ? "#90cdf4" : "#3182ce"} !important;
    }
    .fb-calendar .react-calendar__tile:disabled { opacity: 0.25 !important; cursor: not-allowed !important; }
    .fb-calendar .react-calendar__navigation { margin-bottom: 8px !important; }

    @keyframes fb-fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fb-scaleIn {
      from { opacity: 0; transform: scale(0.94); }
      to   { opacity: 1; transform: scale(1); }
    }
    @keyframes fb-pulse-ring {
      0%   { transform: scale(0.9); opacity: 1; }
      100% { transform: scale(1.6); opacity: 0; }
    }
    @keyframes fb-checkDraw {
      from { stroke-dashoffset: 60; }
      to   { stroke-dashoffset: 0; }
    }
    .fb-fadeUp  { animation: fb-fadeUp 0.38s cubic-bezier(.22,.68,0,1.2) both; }
    .fb-scaleIn { animation: fb-scaleIn 0.32s cubic-bezier(.22,.68,0,1.2) both; }
    .fb-delay-1 { animation-delay: 0.06s; }
    .fb-delay-2 { animation-delay: 0.12s; }
    .fb-delay-3 { animation-delay: 0.18s; }
  `}</style>
)

// ─────────────────────────────────────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────────────────────────────────────
const StepIndicator = ({ step, isDark, textMuted, textPrimary }) => {
    const steps = [
        { num: 1, label: "Giriş" },
        { num: 2, label: "Uzman" },
        { num: 3, label: "Hizmet" },
        { num: 4, label: "Tarih & Saat" },
        { num: 5, label: "Onay" },
    ]

    return (
        <Flex align="flex-start" justify="center" w="100%" mb={6} px={2}>
            {steps.map((s, idx) => {
                const isActive = step === s.num
                const isDone = step > s.num
                const isIdle = step < s.num
                return (
                    <Flex key={s.num} align="flex-start" flex={idx < steps.length - 1 ? 1 : undefined}>
                        <Flex direction="column" align="center" minW="44px">
                            <Box
                                w="34px"
                                h="34px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                fontWeight="700"
                                fontSize="xs"
                                color="white"
                                transition="all 0.3s"
                                style={{
                                    background: isDone
                                        ? "linear-gradient(135deg, #43e97b, #38f9d7)"
                                        : isActive
                                            ? "linear-gradient(135deg, #667eea, #764ba2)"
                                            : isDark
                                                ? "rgba(255,255,255,0.07)"
                                                : "rgba(0,0,0,0.06)",
                                    boxShadow: isActive ? "0 0 0 4px rgba(102,126,234,0.22)" : "none",
                                }}
                            >
                                {isDone ? (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                ) : (
                                    <Text
                                        fontSize="xs"
                                        fontWeight="700"
                                        color={isIdle ? (isDark ? "gray.400" : "gray.500") : "white"}
                                    >
                                        {s.num}
                                    </Text>
                                )}
                            </Box>
                            <Text
                                fontSize="9px"
                                fontWeight={isActive ? "700" : "500"}
                                color={isActive ? textPrimary : textMuted}
                                mt="5px"
                                textAlign="center"
                                whiteSpace="nowrap"
                                display={{ base: "none", sm: "block" }}
                            >
                                {s.label}
                            </Text>
                        </Flex>
                        {idx < steps.length - 1 && (
                            <Box
                                flex={1}
                                h="2px"
                                mt="16px"
                                mx={1}
                                borderRadius="full"
                                style={{
                                    background: isDone
                                        ? "linear-gradient(135deg,#667eea,#764ba2)"
                                        : isDark
                                            ? "rgba(255,255,255,0.08)"
                                            : "rgba(0,0,0,0.08)",
                                    transition: "background 0.4s",
                                }}
                            />
                        )}
                    </Flex>
                )
            })}
        </Flex>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// SKELETONS
// ─────────────────────────────────────────────────────────────────────────────
const StaffSkeleton = ({ cardBg, cardBorder }) => (
    <VStack gap={3} w="100%">
        {[1, 2, 3].map((i) => (
            <Flex
                key={i}
                w="100%"
                bg={cardBg}
                border="1px solid"
                borderColor={cardBorder}
                borderRadius="16px"
                p={4}
                align="center"
                gap={4}
            >
                <SkeletonCircle size="50px" />
                <VStack align="start" gap={2} flex={1}>
                    <Skeleton height="14px" width="45%" borderRadius="8px" />
                    <Skeleton height="11px" width="30%" borderRadius="8px" />
                </VStack>
                <Skeleton height="28px" width="60px" borderRadius="12px" />
            </Flex>
        ))}
    </VStack>
)

const ServiceSkeleton = () => (
    <VStack gap={3} w="100%">
        {[1, 2, 3, 4].map((i) => (
            <Flex key={i} w="100%" p={4} borderRadius="14px" gap={3} align="center">
                <Skeleton height="36px" width="36px" borderRadius="10px" />
                <VStack align="start" gap={2} flex={1}>
                    <Skeleton height="13px" width="55%" borderRadius="7px" />
                    <Skeleton height="10px" width="35%" borderRadius="7px" />
                </VStack>
                <Skeleton height="22px" width="52px" borderRadius="20px" />
            </Flex>
        ))}
    </VStack>
)

const TimeSkeleton = () => (
    <SimpleGrid columns={4} gap={2} w="100%">
        {[...Array(12)].map((_, i) => (
            <Skeleton key={i} height="44px" borderRadius="10px" />
        ))}
    </SimpleGrid>
)

// ─────────────────────────────────────────────────────────────────────────────
// BACK BUTTON
// ─────────────────────────────────────────────────────────────────────────────
const BackButton = ({ onClick, textSecondary, hoverBg, textPrimary }) => (
    <Box
        as="button"
        onClick={onClick}
        w="34px"
        h="34px"
        borderRadius="12px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        color={textSecondary}
        transition="all 0.2s"
        _hover={{ bg: hoverBg, color: textPrimary }}
        aria-label="Geri"
        style={{ cursor: "pointer", border: "none", background: "transparent" }}
    >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    </Box>
)

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const FastBooking = () => {
    const { login } = useAuth()
    const nextButtonRef = useRef(null)

    // ── Tüm color-mode token'ları en üstte, koşulsuz olarak tanımlanıyor ──
    const isDark = useColorModeValue(false, true)
    const pageBg = useColorModeValue(
        "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #f0fff4 100%)",
        "linear-gradient(135deg, #0f0c29 0%, #110c2e 50%, #0a1628 100%)"
    )
    const cardBg = useColorModeValue("white", "rgba(255,255,255,0.04)")
    const cardBorder = useColorModeValue("rgba(0,0,0,0.07)", "rgba(255,255,255,0.08)")
    const textPrimary = useColorModeValue("gray.800", "gray.100")
    const textSecondary = useColorModeValue("gray.500", "gray.400")
    const textMuted = useColorModeValue("gray.400", "gray.500")
    const inputBg = useColorModeValue("gray.50", "rgba(255,255,255,0.05)")
    const inputBorder = useColorModeValue("gray.200", "rgba(255,255,255,0.1)")
    const inputFocusBorder = useColorModeValue("purple.400", "purple.300")
    const dividerColor = useColorModeValue("gray.100", "rgba(255,255,255,0.06)")
    const hoverBg = useColorModeValue("gray.50", "rgba(255,255,255,0.04)")
    const selectedBg = useColorModeValue("purple.50", "rgba(102,126,234,0.12)")
    const selectedBorder = useColorModeValue("purple.400", "purple.400")
    const genderIdleColor = useColorModeValue("gray.600", "gray.400")
    const genderPillBg = useColorModeValue("gray.100", "rgba(255,255,255,0.05)")
    const timeSlotBg = useColorModeValue("gray.50", "rgba(255,255,255,0.04)")
    const timeSlotBorder = useColorModeValue("gray.200", "rgba(255,255,255,0.08)")
    const footerColor = useColorModeValue("gray.400", "gray.600")
    const noteAreaBg = useColorModeValue("white", "rgba(255,255,255,0.04)")
    const badgeBg = useColorModeValue("purple.100", "rgba(102,126,234,0.25)")
    const badgeColor = useColorModeValue("purple.700", "purple.200")
    const summaryCardBg = useColorModeValue("white", "rgba(255,255,255,0.04)")
    const errorAlertBg = useColorModeValue("red.50", "rgba(254,178,178,0.08)")
    const errorAlertBorder = useColorModeValue("red.200", "rgba(254,178,178,0.2)")
    const errorAlertColor = useColorModeValue("red.700", "red.300")
    const infoBoxBg = useColorModeValue("purple.50", "rgba(102,126,234,0.08)")
    const infoBoxBorder = useColorModeValue("purple.100", "rgba(102,126,234,0.18)")
    const infoLinkBg = useColorModeValue("white", "rgba(0,0,0,0.25)")
    const infoLinkBorder = useColorModeValue("gray.100", "rgba(255,255,255,0.06)")
    const infoIconColor = useColorModeValue("purple.500", "purple.300")
    const extraSectionBg = useColorModeValue("gray.50", "rgba(255,255,255,0.03)")
    const extraSectionBorder = useColorModeValue("purple.200", "rgba(102,126,234,0.2)")
    const progressBg = useColorModeValue("gray.100", "rgba(255,255,255,0.06)")
    const timeEmptyColor = useColorModeValue("gray.400", "gray.500")
    const summaryIconBg = useColorModeValue("purple.50", "rgba(102,126,234,0.15)")
    const summaryIconColor = useColorModeValue("purple.500", "purple.300")
    const accentGradient = "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
    const successGradient = "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"

    // ── State ──
    const [step, setStep] = useState(1)
    const [loading, setLoading] = useState(false)
    const [isTimeLoading, setIsTimeLoading] = useState(false)
    const [isServicesLoading, setIsServicesLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState(null)

    const [userInfo, setUserInfo] = useState({ name: "", surname: "", phone: "" })
    const [authenticatedUser, setAuthenticatedUser] = useState(null)
    const [staffList, setStaffList] = useState([])
    const [mainServices, setMainServices] = useState([])
    const [optionalServices, setOptionalServices] = useState([])
    const [genderFilter, setGenderFilter] = useState("all")

    const [selectedStaff, setSelectedStaff] = useState(null)
    const [selectedService, setSelectedService] = useState(null)
    const [selectedOptionalServices, setSelectedOptionalServices] = useState([])

    const [selectedDate, setSelectedDate] = useState(new Date())
    const [timeSlots, setTimeSlots] = useState([])
    const [selectedTime, setSelectedTime] = useState(null)
    const [note, setNote] = useState("")

    const maxDateLimit = new Date()
    maxDateLimit.setDate(maxDateLimit.getDate() + 60)

    // ── STEP 1: FAST LOGIN ──
    const handleInfoSubmit = async (e) => {
        e.preventDefault()
        setError(null)
        const cleanPhone = userInfo.phone.replace(/\D/g, "")
        if (cleanPhone.length !== 10 || !cleanPhone.startsWith("5")) {
            setError("Telefon numaranız 5 ile başlamalı ve 10 haneli olmalıdır.")
            return
        }
        setLoading(true)
        try {
            const payload = { name: userInfo.name, surname: userInfo.surname, phone: "+90" + cleanPhone }
            const response = await api.post("/fast-login", payload)
            const data = response.data || response
            if (data.token) {
                login(data.user, data.token)
                setAuthenticatedUser(data.user)
                await fetchStaffList()
                setStep(2)
            } else {
                throw new Error("Giriş başarılı ancak jeton alınamadı.")
            }
        } catch (err) {
            setError(err.response?.data?.message || "Giriş yapılamadı. Bilgilerinizi kontrol ediniz.")
        } finally {
            setLoading(false)
        }
    }

    // ── PERSONEL ──
    const fetchStaffList = async () => {
        try {
            const res = await usersAPI.getStaff()
            const staffData = res.staff || res.data?.staff || []
            const staff = staffData.map((s) => ({
                ...s,
                schedule: transformWorkDaysToSchedule(s.workDays || []),
            }))
            setStaffList(staff)
        } catch {
            setStaffList([])
        }
    }

    const handleStaffSelect = async (staff) => {
        setSelectedStaff(staff)
        setSelectedService(null)
        setSelectedOptionalServices([])
        setGenderFilter("all")
        setStep(3)
        setIsServicesLoading(true)
        try {
            const response = await api.get(`/services?staff_id=${staff.id}`)
            const data = response.data || response
            if (data.success && data.services) {
                setMainServices(data.services.filter((s) => !s.optional))
                setOptionalServices(data.services.filter((s) => s.optional))
            }
        } catch {
            setError("Bu personelin hizmet bilgileri alınamadı.")
        } finally {
            setIsServicesLoading(false)
        }
    }

    // ── HİZMET ──
    const handleServiceSelect = (service) => {
        setSelectedService(service)
        setSelectedOptionalServices([])
        setTimeout(() => {
            nextButtonRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
        }, 150)
    }

    const handleToggleOptionalService = (service) => {
        setSelectedOptionalServices((prev) => {
            const isAlreadySelected = prev.some((s) => s.id === service.id)
            if (isAlreadySelected) return prev.filter((s) => s.id !== service.id)
            if (prev.length < 2) return [...prev, service]
            setError("En fazla 2 ekstra işlem seçebilirsiniz.")
            setTimeout(() => setError(null), 3000)
            return prev
        })
    }

    const handleProceedToTime = () => {
        if (selectedService) setStep(4)
    }

    // ── SAAT SLOTLARI — Chain Check / Ghost Slots birebir korundu ──
    useEffect(() => {
        const fetchTimeSlots = async () => {
            if (step !== 4 || !selectedStaff || !selectedService) return
            setIsTimeLoading(true)
            setTimeSlots([])
            setError(null)
            setSelectedTime(null)
            try {
                const selectedDateStr = formatDate(selectedDate)
                const response = await workScheduleAPI.getAvailableSlots(selectedStaff.id, selectedDateStr)
                const slotsForDate = response.available_slots?.[selectedDateStr] || []
                if (slotsForDate.length === 0) {
                    setError(`${selectedDateStr} tarihi için randevu doludur.`)
                    return
                }
                let totalDuration = parseInt(selectedService.duration) || 0
                selectedOptionalServices.forEach((opt) => (totalDuration += parseInt(opt.duration) || 0))
                const now = new Date()
                const isToday = selectedDate.toDateString() === now.toDateString()
                const currentTotalMinutes = now.getHours() * 60 + now.getMinutes()
                const sortedSlots = slotsForDate.sort(
                    (a, b) => timeToMinutes(a.start_time || a.time) - timeToMinutes(b.start_time || b.time)
                )
                let finalDisplayList = []
                sortedSlots.forEach((slot, index) => {
                    const timeStr = slot.start_time || slot.time
                    if (!timeStr) return
                    const slotStartMinutes = timeToMinutes(timeStr)
                    if (slot.old_time && slot.old_time !== timeStr) {
                        const isAlreadyThere = finalDisplayList.some((f) => f.time === slot.old_time.substring(0, 5))
                        if (!isAlreadyThere) {
                            finalDisplayList.push({ time: slot.old_time.substring(0, 5), isAvailable: false, isGhost: true, workHourId: `ghost-${slot.id}` })
                        }
                    }
                    let isPastTime = isToday && slotStartMinutes <= currentTotalMinutes
                    let isSlotAvailable =
                        !isPastTime &&
                        (slot.is_available === true || slot.is_available === 1 || slot.is_available === "1")
                    if (isSlotAvailable) {
                        const requiredEndTime = slotStartMinutes + totalDuration
                        let currentChainTime = slotStartMinutes
                        let chainBroken = false
                        for (let i = index; i < sortedSlots.length; i++) {
                            const checkSlot = sortedSlots[i]
                            const checkStart = timeToMinutes(checkSlot.start_time || checkSlot.time)
                            const checkEnd = checkSlot.end_time ? timeToMinutes(checkSlot.end_time) : checkStart + 15
                            if (checkStart > currentChainTime + 1) { chainBroken = true; break }
                            const isCheckAvailable =
                                checkSlot.is_available === true || checkSlot.is_available === 1 || checkSlot.is_available === "1"
                            if (!isCheckAvailable) { chainBroken = true; break }
                            currentChainTime = checkEnd
                            if (currentChainTime >= requiredEndTime) break
                        }
                        if (chainBroken || currentChainTime < requiredEndTime) isSlotAvailable = false
                    }
                    finalDisplayList.push({ time: timeStr.substring(0, 5), isAvailable: isSlotAvailable, isGhost: false, workHourId: slot.id })
                })
                finalDisplayList.sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time))
                setTimeSlots(finalDisplayList)
                if (finalDisplayList.filter((s) => s.isAvailable).length === 0)
                    setError("Seçilen işlem süresi için uygun boşluk bulunamadı.")
            } catch {
                setError("Saatler yüklenirken hata oluştu.")
            } finally {
                setIsTimeLoading(false)
            }
        }
        fetchTimeSlots()
    }, [selectedDate, step, selectedService, selectedStaff, selectedOptionalServices])

    const isDayDisabled = ({ date, view }) => {
        if (view === "month") {
            const today = new Date(); today.setHours(0, 0, 0, 0)
            const checkDate = new Date(date); checkDate.setHours(0, 0, 0, 0)
            if (checkDate < today) return true
            if (selectedStaff) {
                if (!selectedStaff.schedule || Object.keys(selectedStaff.schedule).length === 0) return false
                const dayNames = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"]
                return !Object.prototype.hasOwnProperty.call(selectedStaff.schedule, dayNames[date.getDay()])
            }
        }
        return false
    }

    // ── RANDEVU ONAYLA ──
    const handleConfirmBooking = async () => {
        if (isSubmitting) return
        setIsSubmitting(true)
        setError(null)
        try {
            const payload = {
                customer_id: authenticatedUser.id,
                staff_id: selectedStaff.id,
                service_id: selectedService.id,
                optional_service_id_1: selectedOptionalServices[0]?.id || null,
                optional_service_id_2: selectedOptionalServices[1]?.id || null,
                appointment_date: formatDate(selectedDate),
                appointment_time: selectedTime,
                note: note,
                status: "pending",
            }
            await appointmentsAPI.create(payload)
            setStep(6)
        } catch (err) {
            setError(err.response?.data?.message || "Randevu oluşturulamadı. Lütfen tekrar deneyin.")
        } finally {
            setIsSubmitting(false)
        }
    }

    // ── Derived ──
    const filteredServices = mainServices
        .filter((s) => {
            if (genderFilter === "all") return true
            if (genderFilter === "male") return s.target_gender === "male" || s.target_gender === "unisex" || !s.target_gender
            if (genderFilter === "female") return s.target_gender === "female" || s.target_gender === "unisex" || !s.target_gender
            return true
        })
        .sort((a, b) => (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0))

    const totalDuration =
        parseInt(selectedService?.duration || 0) +
        selectedOptionalServices.reduce((sum, s) => sum + parseInt(s.duration || 0), 0)

    const progressValue = step <= 5 ? (step / 5) * 100 : 100

    const copyLink = () => {
        const url = (typeof window !== "undefined" ? window.location.origin : "") + "/customer/dashboard"
        navigator.clipboard.writeText(url).then(() => {
            Toaster.create({ title: "Link kopyalandı!", type: "success", duration: 2000 })
        })
    }

    // ── Shared style helpers ──
    const ctaButton = (active) => ({
        w: "100%",
        h: "52px",
        bg: active ? accentGradient : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
        color: active ? "white" : isDark ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
        fontWeight: "700",
        fontSize: "sm",
        letterSpacing: "1px",
        textTransform: "uppercase",
        borderRadius: "14px",
        border: "none",
        cursor: active ? "pointer" : "not-allowed",
        transition: "all 0.25s",
        style: { background: active ? accentGradient : undefined },
    })

    // ── RENDER ──
    return (
        <Box
            className="fb-root"
            minH="100vh"
            display="flex"
            flexDirection="column"
            alignItems="center"
            style={{ background: pageBg }}
        >
            <GlobalStyles isDark={isDark} />

            {/* ── HEADER ── */}
            <Box w="100%" maxW="520px" pt={{ base: "24px", md: "40px" }} pb="8px" px={4} textAlign="center">
                <Text
                    className="fb-elegant"
                    fontSize={{ base: "2xl", md: "3xl" }}
                    fontWeight="600"
                    color={textPrimary}
                    style={{ letterSpacing: "2px", fontStyle: "italic" }}
                >
                    {FASTBOOKING_TEXT_TOP}
                </Text>
                <Text
                    fontSize="10px"
                    color={textMuted}
                    style={{ letterSpacing: "4px", textTransform: "uppercase", marginTop: "2px" }}
                >
                    {FASTBOOKING_TEXT_TOP_UNDER}
                </Text>

                {step > 1 && step < 6 && (
                    <Box mt={4} mb={2}>
                        <Box
                            w="100%"
                            h="4px"
                            borderRadius="full"
                            style={{ background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" }}
                            overflow="hidden"
                        >
                            <Box
                                h="100%"
                                borderRadius="full"
                                style={{
                                    width: `${progressValue}%`,
                                    background: accentGradient,
                                    transition: "width 0.5s cubic-bezier(.22,.68,0,1.2)",
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── MAIN ── */}
            <Box w="100%" maxW="520px" px={{ base: "12px", md: "16px" }} pb="80px" flex={1} display="flex" flexDirection="column">
                {step > 1 && step < 6 && (
                    <Box mt={4}>
                        <StepIndicator step={step} isDark={isDark} textMuted={textMuted} textPrimary={textPrimary} />
                    </Box>
                )}

                {/* Error */}
                {error && (
                    <Box
                        bg={errorAlertBg}
                        border="1px solid"
                        borderColor={errorAlertBorder}
                        borderRadius="12px"
                        p={4}
                        mb={4}
                        className="fb-fadeUp"
                        display="flex"
                        alignItems="center"
                        gap={3}
                    >
                        <Box color={errorAlertColor} flexShrink={0}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </Box>
                        <Text fontSize="sm" fontWeight="500" color={errorAlertColor}>{error}</Text>
                    </Box>
                )}

                {/* ══════════════════════════════════════════
            STEP 1 — GİRİŞ
        ══════════════════════════════════════════ */}
                {step === 1 && (
                    <Flex direction="column" justify="center" flex={1} minH="55vh" className="fb-fadeUp">
                        <Box textAlign="center" mb={8}>
                            <Text
                                className="fb-elegant"
                                fontSize={{ base: "2xl", md: "2.8rem" }}
                                fontWeight="400"
                                color={textPrimary}
                                lineHeight="1.2"
                                mb={2}
                            >
                                {FASTBOOKING_TEXT_GENERAL_1}
                            </Text>
                            <Text fontSize="sm" color={textSecondary}>
                                Hızlı randevu için bilgilerinizi giriniz.
                            </Text>
                        </Box>

                        <Box
                            as="form"
                            onSubmit={handleInfoSubmit}
                            bg={cardBg}
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="24px"
                            p={{ base: "20px", md: "28px" }}
                            style={{ boxShadow: isDark ? "0 8px 40px rgba(0,0,0,0.4)" : "0 8px 40px rgba(0,0,0,0.08)" }}
                        >
                            <VStack gap={4}>
                                {/* Ad */}
                                <Box w="100%">
                                    <Text fontSize="xs" fontWeight="600" color={textMuted} mb="6px" style={{ letterSpacing: "0.5px" }}>
                                        ADINIZ
                                    </Text>
                                    <Input
                                        placeholder="Adınız"
                                        value={userInfo.name}
                                        onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
                                        required
                                        style={{
                                            background: inputBg,
                                            border: `1.5px solid ${inputBorder}`,
                                            borderRadius: "12px",
                                            height: "48px",
                                            fontSize: "0.875rem",
                                            fontWeight: "500",
                                            color: textPrimary,
                                            width: "100%",
                                            padding: "0 16px",
                                            outline: "none",
                                            transition: "all 0.2s",
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = isDark ? "#b794f4" : "#9f7aea"
                                            e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.15)"
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = inputBorder
                                            e.target.style.boxShadow = "none"
                                        }}
                                    />
                                </Box>

                                {/* Soyad */}
                                <Box w="100%">
                                    <Text fontSize="xs" fontWeight="600" color={textMuted} mb="6px" style={{ letterSpacing: "0.5px" }}>
                                        SOYADINIZ
                                    </Text>
                                    <Input
                                        placeholder="Soyadınız"
                                        value={userInfo.surname}
                                        onChange={(e) => setUserInfo({ ...userInfo, surname: e.target.value })}
                                        required
                                        style={{
                                            background: inputBg,
                                            border: `1.5px solid ${inputBorder}`,
                                            borderRadius: "12px",
                                            height: "48px",
                                            fontSize: "0.875rem",
                                            fontWeight: "500",
                                            color: textPrimary,
                                            width: "100%",
                                            padding: "0 16px",
                                            outline: "none",
                                            transition: "all 0.2s",
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = isDark ? "#b794f4" : "#9f7aea"
                                            e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.15)"
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = inputBorder
                                            e.target.style.boxShadow = "none"
                                        }}
                                    />
                                </Box>

                                {/* Telefon */}
                                <Box w="100%">
                                    <Text fontSize="xs" fontWeight="600" color={textMuted} mb="6px" style={{ letterSpacing: "0.5px" }}>
                                        TELEFON NUMARANIZ
                                    </Text>
                                    <Flex>
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="center"
                                            px={4}
                                            style={{
                                                background: isDark ? "rgba(102,126,234,0.18)" : "#faf5ff",
                                                border: `1.5px solid ${isDark ? "rgba(102,126,234,0.3)" : "#d6bcfa"}`,
                                                borderRight: "none",
                                                borderRadius: "12px 0 0 12px",
                                                height: "48px",
                                                color: isDark ? "#d6bcfa" : "#553c9a",
                                                fontWeight: "700",
                                                fontSize: "0.875rem",
                                                whiteSpace: "nowrap",
                                                flexShrink: 0,
                                            }}
                                        >
                                            +90
                                        </Box>
                                        <Input
                                            placeholder="5XX XXX XX XX"
                                            type="tel"
                                            maxLength={10}
                                            value={userInfo.phone}
                                            onChange={(e) => setUserInfo({ ...userInfo, phone: e.target.value })}
                                            required
                                            style={{
                                                background: inputBg,
                                                border: `1.5px solid ${inputBorder}`,
                                                borderLeft: "none",
                                                borderRadius: "0 12px 12px 0",
                                                height: "48px",
                                                fontSize: "0.875rem",
                                                fontWeight: "500",
                                                color: textPrimary,
                                                width: "100%",
                                                padding: "0 16px",
                                                outline: "none",
                                                transition: "all 0.2s",
                                                flex: 1,
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = isDark ? "#b794f4" : "#9f7aea"
                                                e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.15)"
                                                e.target.style.zIndex = "1"
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = inputBorder
                                                e.target.style.boxShadow = "none"
                                                e.target.style.zIndex = "0"
                                            }}
                                        />
                                    </Flex>
                                    <Text fontSize="10px" color={textMuted} mt="6px" ml="2px">
                                        Başında 0 olmadan, 10 haneli giriniz.
                                    </Text>
                                </Box>

                                {/* Submit */}
                                <Box
                                    as="button"
                                    type="submit"
                                    w="100%"
                                    h="52px"
                                    borderRadius="14px"
                                    border="none"
                                    cursor={loading ? "not-allowed" : "pointer"}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    gap={2}
                                    fontWeight="700"
                                    fontSize="sm"
                                    color="white"
                                    mt={1}
                                    style={{
                                        background: accentGradient,
                                        letterSpacing: "1.5px",
                                        textTransform: "uppercase",
                                        transition: "all 0.25s",
                                        opacity: loading ? 0.8 : 1,
                                    }}
                                    onMouseEnter={(e) => !loading && (e.currentTarget.style.transform = "translateY(-2px)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                                >
                                    {loading ? <Spinner size="sm" color="white" /> : "Devam Et"}
                                </Box>
                            </VStack>
                        </Box>
                    </Flex>
                )}

                {/* ══════════════════════════════════════════
            STEP 2 — UZMAN SEÇİMİ
        ══════════════════════════════════════════ */}
                {step === 2 && (
                    <Box className="fb-fadeUp">
                        <Text
                            fontSize="xs"
                            fontWeight="700"
                            color={textMuted}
                            style={{ letterSpacing: "1.5px", textTransform: "uppercase", marginBottom: "16px" }}
                        >
                            Hizmet almak istediğiniz uzmanı seçin
                        </Text>

                        {staffList.length === 0 ? (
                            <StaffSkeleton cardBg={cardBg} cardBorder={cardBorder} />
                        ) : (
                            <VStack gap={3}>
                                {staffList
                                    .filter((s) => !s.name.toLowerCase().includes("izinli"))
                                    .map((staff, idx) => (
                                        <Box
                                            key={staff.id}
                                            as="button"
                                            w="100%"
                                            onClick={() => handleStaffSelect(staff)}
                                            bg={cardBg}
                                            border="1.5px solid"
                                            borderColor={cardBorder}
                                            borderRadius="18px"
                                            p={4}
                                            display="flex"
                                            alignItems="center"
                                            gap={4}
                                            cursor="pointer"
                                            transition="all 0.22s"
                                            style={{ textAlign: "left" }}
                                            _hover={{
                                                borderColor: "purple.400",
                                                bg: selectedBg,
                                                transform: "translateY(-2px)",
                                            }}
                                            _focus={{ outline: "none", boxShadow: "0 0 0 3px rgba(102,126,234,0.22)" }}
                                            className={`fb-fadeUp fb-delay-${Math.min(idx + 1, 3)}`}
                                        >
                                            <Box position="relative" flexShrink={0}>
                                                <Box
                                                    w="48px"
                                                    h="48px"
                                                    borderRadius="full"
                                                    overflow="hidden"
                                                    border="2px solid"
                                                    borderColor={isDark ? "rgba(102,126,234,0.3)" : "#d6bcfa"}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    style={{
                                                        background: staff.picture
                                                            ? "transparent"
                                                            : isDark
                                                                ? "rgba(102,126,234,0.22)"
                                                                : "#faf5ff",
                                                    }}
                                                >
                                                    {staff.picture ? (
                                                        <img
                                                            src={staff.picture}
                                                            alt={staff.name}
                                                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <Text
                                                            fontWeight="700"
                                                            fontSize="sm"
                                                            color={isDark ? "#d6bcfa" : "#553c9a"}
                                                        >
                                                            {getInitials(staff.name)}
                                                        </Text>
                                                    )}
                                                </Box>
                                                <Box
                                                    position="absolute"
                                                    bottom="1px"
                                                    right="1px"
                                                    w="11px"
                                                    h="11px"
                                                    borderRadius="full"
                                                    bg="green.400"
                                                    border="2px solid"
                                                    borderColor={cardBg}
                                                />
                                            </Box>

                                            <Box flex={1} minW={0}>
                                                <Text fontSize="sm" fontWeight="700" color={textPrimary} noOfLines={1}>
                                                    {staff.name} {staff.surname}
                                                </Text>
                                                <Text fontSize="xs" color={textSecondary} mt="2px" noOfLines={1}>
                                                    {staff.tag || "Saç Tasarım Uzmanı"}
                                                </Text>
                                            </Box>

                                            <VStack gap={1} align="flex-end" flexShrink={0}>
                                                <HStack gap={1}>
                                                    <Box color="orange.400">
                                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                                        </svg>
                                                    </Box>
                                                    <Text fontSize="xs" fontWeight="700" color={textSecondary}>{staff.rating}</Text>
                                                </HStack>
                                                <Box color={textMuted}>
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                                        <polyline points="9 18 15 12 9 6" />
                                                    </svg>
                                                </Box>
                                            </VStack>
                                        </Box>
                                    ))}
                            </VStack>
                        )}
                    </Box>
                )}

                {/* ══════════════════════════════════════════
            STEP 3 — HİZMET & EKSTRALAR
        ══════════════════════════════════════════ */}
                {step === 3 && (
                    <Box className="fb-fadeUp">
                        <HStack mb={4}>
                            <BackButton onClick={() => setStep(2)} textSecondary={textSecondary} hoverBg={hoverBg} textPrimary={textPrimary} />
                            <Text fontSize="xs" fontWeight="700" color={textMuted} style={{ letterSpacing: "1.5px", textTransform: "uppercase" }}>
                                Hizmet & Ekstralar
                            </Text>
                        </HStack>

                        {/* Cinsiyet Filtresi */}
                        <Flex
                            mb={4}
                            p="4px"
                            borderRadius="12px"
                            w="fit-content"
                            gap={1}
                            style={{ background: genderPillBg }}
                        >
                            {[
                                { value: "all", label: "Tümü" },
                                { value: "male", label: "Erkek" },
                                { value: "female", label: "Kadın" },
                            ].map((f) => (
                                <Box
                                    key={f.value}
                                    as="button"
                                    h="32px"
                                    px={4}
                                    borderRadius="9px"
                                    border="none"
                                    cursor="pointer"
                                    fontWeight={genderFilter === f.value ? "700" : "500"}
                                    fontSize="xs"
                                    color={genderFilter === f.value ? "white" : genderIdleColor}
                                    onClick={() => setGenderFilter(f.value)}
                                    style={{
                                        background: genderFilter === f.value ? accentGradient : "transparent",
                                        boxShadow: genderFilter === f.value ? "0 2px 8px rgba(102,126,234,0.3)" : "none",
                                        transition: "all 0.2s",
                                    }}
                                >
                                    {f.label}
                                </Box>
                            ))}
                        </Flex>

                        {isServicesLoading ? (
                            <ServiceSkeleton />
                        ) : (
                            <>
                                <VStack gap={2} mb={4}>
                                    {filteredServices.map((service, idx) => {
                                        const isSelected = selectedService?.id === service.id
                                        return (
                                            <Box
                                                key={service.id}
                                                as="button"
                                                w="100%"
                                                onClick={() => handleServiceSelect(service)}
                                                border="1.5px solid"
                                                borderColor={isSelected ? selectedBorder : cardBorder}
                                                borderRadius="16px"
                                                p={4}
                                                display="flex"
                                                alignItems="center"
                                                gap={3}
                                                cursor="pointer"
                                                transition="all 0.2s"
                                                style={{
                                                    background: isSelected ? selectedBg : cardBg,
                                                    textAlign: "left",
                                                    boxShadow: isSelected ? "0 4px 18px rgba(102,126,234,0.15)" : "none",
                                                }}
                                                _hover={{ borderColor: "purple.400" }}
                                                _focus={{ outline: "none", boxShadow: "0 0 0 3px rgba(102,126,234,0.2)" }}
                                                className={`fb-fadeUp fb-delay-${Math.min(idx + 1, 3)}`}
                                            >
                                                {/* Checkbox circle */}
                                                <Box
                                                    w="28px"
                                                    h="28px"
                                                    borderRadius="full"
                                                    flexShrink={0}
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    style={{
                                                        background: isSelected ? accentGradient : inputBg,
                                                        border: `1.5px solid ${isSelected ? "transparent" : inputBorder}`,
                                                        transition: "all 0.2s",
                                                    }}
                                                >
                                                    {isSelected && (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                            <polyline points="20 6 9 17 4 12" />
                                                        </svg>
                                                    )}
                                                </Box>

                                                <Box flex={1} minW={0}>
                                                    <HStack gap={2} flexWrap="wrap">
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="700"
                                                            color={isSelected ? (isDark ? "#d6bcfa" : "#553c9a") : textPrimary}
                                                            noOfLines={1}
                                                        >
                                                            {service.name}
                                                        </Text>
                                                        {service.is_favorite && (
                                                            <Box
                                                                px="8px"
                                                                py="2px"
                                                                borderRadius="full"
                                                                fontSize="9px"
                                                                fontWeight="700"
                                                                style={{ background: badgeBg, color: badgeColor }}
                                                            >
                                                                ⭐ Popüler
                                                            </Box>
                                                        )}
                                                    </HStack>
                                                    {service.description && (
                                                        <Text fontSize="11px" color={textMuted} mt="2px" noOfLines={1}>
                                                            {service.description}
                                                        </Text>
                                                    )}
                                                </Box>

                                                <VStack gap="2px" align="flex-end" flexShrink={0}>
                                                    {service.price && (
                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="800"
                                                            color={isSelected ? (isDark ? "#d6bcfa" : "#553c9a") : textPrimary}
                                                        >
                                                            {service.price}₺
                                                        </Text>
                                                    )}
                                                    <HStack gap={1}>
                                                        <Box color={textMuted}>
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                                                            </svg>
                                                        </Box>
                                                        <Text fontSize="10px" color={textMuted} fontWeight="600">{service.duration} dk</Text>
                                                    </HStack>
                                                </VStack>
                                            </Box>
                                        )
                                    })}
                                </VStack>

                                {/* Ekstra Hizmetler */}
                                {selectedService && optionalServices.length > 0 && (
                                    <Box
                                        mb={4}
                                        p={4}
                                        border="1.5px dashed"
                                        borderColor={extraSectionBorder}
                                        borderRadius="16px"
                                        className="fb-scaleIn"
                                        style={{ background: extraSectionBg }}
                                    >
                                        <Text
                                            fontSize="xs"
                                            fontWeight="700"
                                            color={textMuted}
                                            mb={3}
                                            style={{ letterSpacing: "1px", textTransform: "uppercase" }}
                                        >
                                            Ekstra İşlemler — İsteğe Bağlı (max 2)
                                        </Text>
                                        <Flex flexWrap="wrap" gap={2}>
                                            {optionalServices.map((opt) => {
                                                const isOptSelected = selectedOptionalServices.some((s) => s.id === opt.id)
                                                return (
                                                    <Box
                                                        key={opt.id}
                                                        as="button"
                                                        py="8px"
                                                        px={3}
                                                        borderRadius="10px"
                                                        border="1.5px solid"
                                                        borderColor={isOptSelected ? "transparent" : inputBorder}
                                                        cursor="pointer"
                                                        fontWeight={isOptSelected ? "700" : "500"}
                                                        fontSize="xs"
                                                        color={isOptSelected ? "white" : textSecondary}
                                                        onClick={() => handleToggleOptionalService(opt)}
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        style={{
                                                            background: isOptSelected ? accentGradient : inputBg,
                                                            boxShadow: isOptSelected ? "0 3px 10px rgba(102,126,234,0.3)" : "none",
                                                            transition: "all 0.2s",
                                                        }}
                                                    >
                                                        {isOptSelected && (
                                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                                                                <polyline points="20 6 9 17 4 12" />
                                                            </svg>
                                                        )}
                                                        {opt.name}
                                                        <Text as="span" style={{ opacity: 0.65, marginLeft: "4px", fontWeight: 400 }}>
                                                            ({opt.duration}dk)
                                                        </Text>
                                                    </Box>
                                                )
                                            })}
                                        </Flex>
                                        {selectedOptionalServices.length > 0 && (
                                            <Text fontSize="10px" color={isDark ? "#d6bcfa" : "#553c9a"} mt={2} fontWeight="600">
                                                {selectedOptionalServices.length}/2 ekstra seçildi
                                            </Text>
                                        )}
                                    </Box>
                                )}

                                {/* Next CTA */}
                                <Box
                                    ref={nextButtonRef}
                                    as="button"
                                    w="100%"
                                    h="52px"
                                    borderRadius="14px"
                                    border="none"
                                    cursor={selectedService ? "pointer" : "not-allowed"}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="700"
                                    fontSize="sm"
                                    color={selectedService ? "white" : textMuted}
                                    onClick={handleProceedToTime}
                                    style={{
                                        background: selectedService ? accentGradient : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                                        letterSpacing: "1px",
                                        textTransform: "uppercase",
                                        transition: "all 0.25s",
                                        opacity: selectedService ? 1 : 0.5,
                                    }}
                                    onMouseEnter={(e) => selectedService && (e.currentTarget.style.transform = "translateY(-2px)")}
                                    onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                                >
                                    Tarih & Saat Seçimine Geç
                                </Box>
                            </>
                        )}
                    </Box>
                )}

                {/* ══════════════════════════════════════════
            STEP 4 — TARİH & SAAT
        ══════════════════════════════════════════ */}
                {step === 4 && (
                    <Box className="fb-fadeUp">
                        <HStack mb={4}>
                            <BackButton onClick={() => setStep(3)} textSecondary={textSecondary} hoverBg={hoverBg} textPrimary={textPrimary} />
                            <Text fontSize="xs" fontWeight="700" color={textMuted} style={{ letterSpacing: "1.5px", textTransform: "uppercase" }}>
                                Tarih & Saat Seçin
                            </Text>
                        </HStack>

                        {/* Calendar */}
                        <Box
                            className="fb-calendar"
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="20px"
                            overflow="hidden"
                            mb={5}
                            style={{ background: cardBg, boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,0,0,0.06)" }}
                        >
                            <Calendar
                                onChange={setSelectedDate}
                                value={selectedDate}
                                tileDisabled={isDayDisabled}
                                maxDate={maxDateLimit}
                                prev2Label={null}
                                next2Label={null}
                            />
                        </Box>

                        {/* Date label */}
                        <Flex justify="space-between" align="center" mb={3} px={1}>
                            <HStack gap={2}>
                                <Box color={isDark ? "#d6bcfa" : "#553c9a"}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                                    </svg>
                                </Box>
                                <Text fontSize="sm" fontWeight="700" color={textPrimary}>
                                    {selectedDate.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long" })}
                                </Text>
                            </HStack>
                            {!isTimeLoading && timeSlots.filter((s) => s.isAvailable).length > 0 && (
                                <Box
                                    px="10px"
                                    py="4px"
                                    borderRadius="full"
                                    fontSize="10px"
                                    fontWeight="700"
                                    style={{ background: badgeBg, color: badgeColor }}
                                >
                                    {timeSlots.filter((s) => s.isAvailable).length} boş saat
                                </Box>
                            )}
                        </Flex>

                        {/* Time slots */}
                        <Box
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="20px"
                            p={4}
                            mb={4}
                            minH="120px"
                            style={{ background: cardBg, boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.25)" : "0 4px 20px rgba(0,0,0,0.04)" }}
                        >
                            {isTimeLoading ? (
                                <Box>
                                    <TimeSkeleton />
                                    <Text fontSize="xs" color={textMuted} textAlign="center" mt={3}>
                                        Müsaitlik kontrol ediliyor...
                                    </Text>
                                </Box>
                            ) : timeSlots.length === 0 ? (
                                <Flex direction="column" align="center" justify="center" py={6} gap={2}>
                                    <Box color={timeEmptyColor} style={{ opacity: 0.6 }}>
                                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                                        </svg>
                                    </Box>
                                    <Text fontSize="sm" color={textMuted}>Bu tarih için uygun saat bulunamadı.</Text>
                                </Flex>
                            ) : (
                                <SimpleGrid columns={4} gap={2}>
                                    {timeSlots.map((slot, index) => {
                                        const isActive = selectedTime === slot.time
                                        const isDisabled = !slot.isAvailable
                                        const isGhost = slot.isGhost
                                        const canClick = slot.isAvailable && !slot.isGhost

                                        let slotStyle = {
                                            background: timeSlotBg,
                                            border: `1.5px solid ${timeSlotBorder}`,
                                            color: textPrimary,
                                            opacity: 1,
                                            cursor: "pointer",
                                            textDecoration: "none",
                                            boxShadow: "none",
                                            transition: "all 0.18s",
                                        }
                                        if (isActive) {
                                            slotStyle.background = accentGradient
                                            slotStyle.border = "1.5px solid transparent"
                                            slotStyle.color = "white"
                                            slotStyle.boxShadow = "0 4px 14px rgba(102,126,234,0.38)"
                                        } else if (isGhost) {
                                            slotStyle.background = isDark ? "rgba(239,68,68,0.06)" : "rgba(239,68,68,0.04)"
                                            slotStyle.border = `1.5px solid ${isDark ? "rgba(239,68,68,0.2)" : "rgba(239,68,68,0.15)"}`
                                            slotStyle.color = textMuted
                                            slotStyle.opacity = 0.5
                                            slotStyle.cursor = "not-allowed"
                                            slotStyle.textDecoration = "line-through"
                                        } else if (isDisabled) {
                                            slotStyle.background = isDark ? "rgba(255,255,255,0.02)" : "#f7fafc"
                                            slotStyle.border = "1.5px solid transparent"
                                            slotStyle.color = textMuted
                                            slotStyle.opacity = 0.3
                                            slotStyle.cursor = "not-allowed"
                                            slotStyle.textDecoration = "line-through"
                                        }

                                        return (
                                            <Box
                                                key={`${slot.time}-${index}`}
                                                as="button"
                                                borderRadius="10px"
                                                py="10px"
                                                px={1}
                                                textAlign="center"
                                                fontSize="xs"
                                                fontWeight={isActive ? "700" : "500"}
                                                onClick={() => canClick && setSelectedTime(slot.time)}
                                                title={slot.isGhost ? "Bu saat dolu veya kaydırılmış" : ""}
                                                style={slotStyle}
                                                onMouseEnter={(e) => {
                                                    if (canClick && !isActive) {
                                                        e.currentTarget.style.transform = "translateY(-1px)"
                                                        e.currentTarget.style.borderColor = isDark ? "#b794f4" : "#9f7aea"
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = "translateY(0)"
                                                    if (!isActive) e.currentTarget.style.borderColor = timeSlotBorder
                                                }}
                                            >
                                                {slot.time}
                                            </Box>
                                        )
                                    })}
                                </SimpleGrid>
                            )}
                        </Box>

                        {/* Next CTA */}
                        <Box
                            as="button"
                            w="100%"
                            h="52px"
                            borderRadius="14px"
                            border="none"
                            cursor={selectedTime ? "pointer" : "not-allowed"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            fontWeight="700"
                            fontSize="sm"
                            color={selectedTime ? "white" : textMuted}
                            onClick={() => selectedTime && setStep(5)}
                            style={{
                                background: selectedTime ? accentGradient : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                                transition: "all 0.25s",
                                opacity: selectedTime ? 1 : 0.5,
                            }}
                            onMouseEnter={(e) => selectedTime && (e.currentTarget.style.transform = "translateY(-2px)")}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                        >
                            Özet & Onaya Geç
                        </Box>
                    </Box>
                )}

                {/* ══════════════════════════════════════════
            STEP 5 — ÖZET & ONAY
        ══════════════════════════════════════════ */}
                {step === 5 && (
                    <Box className="fb-fadeUp">
                        <HStack mb={5}>
                            <BackButton onClick={() => setStep(4)} textSecondary={textSecondary} hoverBg={hoverBg} textPrimary={textPrimary} />
                            <Text fontSize="xs" fontWeight="700" color={textMuted} style={{ letterSpacing: "1.5px", textTransform: "uppercase" }}>
                                Son Kontrol
                            </Text>
                        </HStack>

                        {/* Summary Card */}
                        <Box
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="22px"
                            overflow="hidden"
                            mb={4}
                            style={{
                                background: cardBg,
                                borderLeft: "4px solid #9f7aea",
                                boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.35)" : "0 8px 32px rgba(0,0,0,0.07)",
                            }}
                        >
                            {/* Staff header */}
                            <Flex align="center" gap={4} p={5} pb={4} borderBottom="1px solid" borderColor={dividerColor}>
                                <Box
                                    w="48px"
                                    h="48px"
                                    borderRadius="full"
                                    overflow="hidden"
                                    border="2px solid"
                                    borderColor={isDark ? "rgba(102,126,234,0.3)" : "#d6bcfa"}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    flexShrink={0}
                                    style={{ background: isDark ? "rgba(102,126,234,0.22)" : "#faf5ff" }}
                                >
                                    {selectedStaff?.picture ? (
                                        <img src={selectedStaff.picture} alt={selectedStaff.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    ) : (
                                        <Text fontWeight="700" fontSize="sm" color={isDark ? "#d6bcfa" : "#553c9a"}>
                                            {getInitials(selectedStaff?.name)}
                                        </Text>
                                    )}
                                </Box>
                                <Box flex={1}>
                                    <Text fontSize="sm" fontWeight="700" color={textPrimary}>{selectedStaff?.name}</Text>
                                    <Text fontSize="xs" color={textMuted}>Seçilen Uzman</Text>
                                </Box>
                                <Box
                                    px="10px"
                                    py="4px"
                                    borderRadius="full"
                                    fontSize="10px"
                                    fontWeight="700"
                                    style={{ background: badgeBg, color: badgeColor }}
                                >
                                    {totalDuration} dk
                                </Box>
                            </Flex>

                            {/* Detail rows */}
                            <VStack gap={0} p={4}>
                                {[
                                    {
                                        icon: (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="6" cy="6" r="3" /><circle cx="6" cy="18" r="3" />
                                                <line x1="20" y1="4" x2="8.12" y2="15.88" />
                                                <line x1="14.47" y1="14.48" x2="20" y2="20" />
                                                <line x1="8.12" y1="8.12" x2="12" y2="12" />
                                            </svg>
                                        ),
                                        label: "Ana Hizmet",
                                        value: selectedService?.name,
                                        extra: selectedService?.price ? `${selectedService.price}₺` : null,
                                        show: true,
                                    },
                                    {
                                        icon: (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <polyline points="12 20h9" />
                                                <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
                                            </svg>
                                        ),
                                        label: "Ekstralar",
                                        value: selectedOptionalServices.map((s) => s.name).join(", "),
                                        extra: null,
                                        show: selectedOptionalServices.length > 0,
                                    },
                                    {
                                        icon: (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <rect x="3" y="4" width="18" height="18" rx="2" />
                                                <line x1="16" y1="2" x2="16" y2="6" />
                                                <line x1="8" y1="2" x2="8" y2="6" />
                                                <line x1="3" y1="10" x2="21" y2="10" />
                                            </svg>
                                        ),
                                        label: "Tarih",
                                        value: selectedDate.toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }),
                                        extra: null,
                                        show: true,
                                    },
                                    {
                                        icon: (
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
                                            </svg>
                                        ),
                                        label: "Saat",
                                        value: selectedTime,
                                        extra: null,
                                        isTime: true,
                                        show: true,
                                    },
                                ]
                                    .filter((r) => r.show)
                                    .map((row, idx, arr) => (
                                        <Box key={row.label} w="100%">
                                            <Flex align="center" justify="space-between" py={3}>
                                                <HStack gap={3}>
                                                    <Box
                                                        w="30px"
                                                        h="30px"
                                                        borderRadius="full"
                                                        flexShrink={0}
                                                        display="flex"
                                                        alignItems="center"
                                                        justifyContent="center"
                                                        style={{ background: summaryIconBg, color: summaryIconColor }}
                                                    >
                                                        {row.icon}
                                                    </Box>
                                                    <Box>
                                                        <Text fontSize="10px" fontWeight="700" color={textMuted} style={{ letterSpacing: "0.8px", textTransform: "uppercase" }}>
                                                            {row.label}
                                                        </Text>
                                                        <Text
                                                            fontSize={row.isTime ? "lg" : "sm"}
                                                            fontWeight={row.isTime ? "800" : "600"}
                                                            color={row.isTime ? (isDark ? "#d6bcfa" : "#553c9a") : textPrimary}
                                                            mt="2px"
                                                            className={row.isTime ? "fb-elegant" : ""}
                                                        >
                                                            {row.value}
                                                        </Text>
                                                    </Box>
                                                </HStack>
                                                {row.extra && (
                                                    <Text fontSize="sm" fontWeight="800" color={isDark ? "#d6bcfa" : "#553c9a"}>
                                                        {row.extra}
                                                    </Text>
                                                )}
                                            </Flex>
                                            {idx < arr.length - 1 && (
                                                <Box h="1px" w="100%" style={{ background: dividerColor }} />
                                            )}
                                        </Box>
                                    ))}
                            </VStack>
                        </Box>

                        {/* Note */}
                        <Box mb={4}>
                            <Text fontSize="xs" fontWeight="700" color={textMuted} mb={2} style={{ letterSpacing: "0.8px", textTransform: "uppercase" }}>
                                Not Ekle (İsteğe Bağlı)
                            </Text>
                            <Textarea
                                placeholder="Özel bir isteğiniz var mı?"
                                rows={2}
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                style={{
                                    background: noteAreaBg,
                                    border: `1.5px solid ${inputBorder}`,
                                    borderRadius: "14px",
                                    fontSize: "0.875rem",
                                    color: textPrimary,
                                    resize: "none",
                                    width: "100%",
                                    padding: "12px 16px",
                                    outline: "none",
                                    transition: "all 0.2s",
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = isDark ? "#b794f4" : "#9f7aea"
                                    e.target.style.boxShadow = "0 0 0 3px rgba(102,126,234,0.15)"
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = inputBorder
                                    e.target.style.boxShadow = "none"
                                }}
                            />
                        </Box>

                        {/* Confirm CTA */}
                        <Box
                            as="button"
                            w="100%"
                            h="56px"
                            borderRadius="16px"
                            border="none"
                            cursor={isSubmitting ? "not-allowed" : "pointer"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            gap={2}
                            fontWeight="700"
                            fontSize="sm"
                            color="white"
                            onClick={handleConfirmBooking}
                            style={{
                                background: accentGradient,
                                letterSpacing: "1px",
                                textTransform: "uppercase",
                                transition: "all 0.25s",
                                opacity: isSubmitting ? 0.8 : 1,
                            }}
                            onMouseEnter={(e) => !isSubmitting && (e.currentTarget.style.transform = "translateY(-2px)")}
                            onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner size="sm" color="white" />
                                    <Text as="span" fontSize="sm" fontWeight="700" color="white">Randevu Oluşturuluyor...</Text>
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                    Randevuyu Onayla
                                </>
                            )}
                        </Box>
                    </Box>
                )}

                {/* ══════════════════════════════════════════
            STEP 6 — TEŞEKKÜR
        ══════════════════════════════════════════ */}
                {step === 6 && (
                    <Flex direction="column" align="center" justify="center" textAlign="center" flex={1} minH="60vh" className="fb-fadeUp" gap={0}>
                        {/* Animated success */}
                        <Box position="relative" mb={6}>
                            <Box
                                w="80px"
                                h="80px"
                                borderRadius="full"
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                style={{ background: successGradient, boxShadow: "0 8px 32px rgba(67,233,123,0.38)" }}
                            >
                                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline
                                        points="20 6 9 17 4 12"
                                        style={{ strokeDasharray: 60, strokeDashoffset: 0, animation: "fb-checkDraw 0.5s ease 0.2s both" }}
                                    />
                                </svg>
                            </Box>
                            {[0.3, 0.6].map((delay) => (
                                <Box
                                    key={delay}
                                    w="80px"
                                    h="80px"
                                    borderRadius="full"
                                    border="2px solid"
                                    borderColor="green.300"
                                    position="absolute"
                                    top="0"
                                    left="0"
                                    style={{
                                        opacity: 0,
                                        animation: `fb-pulse-ring 1.5s cubic-bezier(0.215,0.61,0.355,1) ${delay}s infinite`,
                                    }}
                                />
                            ))}
                        </Box>

                        <Text className="fb-elegant" fontSize="2xl" fontWeight="600" color={textPrimary} mb={2}>
                            Teşekkürler!
                        </Text>
                        <Text fontSize="sm" color={textSecondary} mb={6} maxW="280px">
                            Randevunuz başarıyla oluşturulmuştur. Görüşmek üzere!
                        </Text>

                        {/* Details */}
                        <Box
                            w="100%"
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="20px"
                            overflow="hidden"
                            mb={5}
                            style={{ background: summaryCardBg, boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.3)" : "0 8px 32px rgba(0,0,0,0.06)" }}
                        >
                            {[
                                { label: "Uzman", value: selectedStaff?.name, highlight: false },
                                { label: "Hizmet", value: selectedService?.name, highlight: false },
                                {
                                    label: "Tarih",
                                    value: selectedDate?.toLocaleDateString("tr-TR", { weekday: "short", day: "numeric", month: "long" }),
                                    highlight: false,
                                },
                                { label: "Saat", value: selectedTime, highlight: true },
                            ].map((row, idx, arr) => (
                                <Flex
                                    key={row.label}
                                    align="center"
                                    justify="space-between"
                                    px={5}
                                    py="14px"
                                    borderBottom={idx < arr.length - 1 ? "1px solid" : "none"}
                                    borderColor={dividerColor}
                                >
                                    <Text fontSize="xs" fontWeight="700" color={textMuted} style={{ textTransform: "uppercase", letterSpacing: "0.8px" }}>
                                        {row.label}
                                    </Text>
                                    <Text
                                        fontSize={row.highlight ? "lg" : "sm"}
                                        fontWeight={row.highlight ? "800" : "600"}
                                        color={row.highlight ? (isDark ? "#68d391" : "#276749") : textPrimary}
                                        className={row.highlight ? "fb-elegant" : ""}
                                    >
                                        {row.value}
                                    </Text>
                                </Flex>
                            ))}
                        </Box>

                        {/* Info box */}
                        <Box
                            w="100%"
                            border="1px solid"
                            borderColor={infoBoxBorder}
                            borderRadius="16px"
                            p={4}
                            mb={5}
                            style={{ background: infoBoxBg }}
                        >
                            <HStack gap={3} align="flex-start">
                                <Box color={infoIconColor} flexShrink={0} mt="2px">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                                    </svg>
                                </Box>
                                <Text fontSize="xs" color={textSecondary} lineHeight="1.6">
                                    Randevularınızı takip etmek veya iptal etmek için panelinizi tarayıcıdan açabilirsiniz.
                                </Text>
                            </HStack>

                            <Flex
                                mt={3}
                                p="10px"
                                borderRadius="10px"
                                border="1px solid"
                                borderColor={infoLinkBorder}
                                justify="space-between"
                                align="center"
                                style={{ background: infoLinkBg }}
                            >
                                <Text fontSize="xs" color={infoIconColor} noOfLines={1} flex={1} style={{ fontFamily: "monospace" }}>
                                    {typeof window !== "undefined" ? window.location.origin + "/customer/dashboard" : "/customer/dashboard"}
                                </Text>
                                <Box
                                    as="button"
                                    w="28px"
                                    h="28px"
                                    borderRadius="8px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    color={infoIconColor}
                                    flexShrink={0}
                                    onClick={copyLink}
                                    style={{ background: "transparent", border: "none", cursor: "pointer", transition: "all 0.2s" }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "rgba(102,126,234,0.15)" : "#faf5ff")}
                                    onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                    title="Linki Kopyala"
                                >
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="9" y="9" width="13" height="13" rx="2" />
                                        <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
                                    </svg>
                                </Box>
                            </Flex>
                        </Box>

                        <Box
                            as="button"
                            px={6}
                            py="10px"
                            borderRadius="12px"
                            border="none"
                            cursor="pointer"
                            fontWeight="700"
                            fontSize="sm"
                            color={infoIconColor}
                            onClick={() => window.location.reload()}
                            style={{ background: "transparent", transition: "all 0.2s" }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = isDark ? "rgba(102,126,234,0.1)" : "#faf5ff")}
                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                        >
                            Yeni Randevu Al
                        </Box>
                    </Flex>
                )}
            </Box>

            {/* ── FOOTER ── */}
            <Box
                w="100%"
                textAlign="center"
                py={5}
                px={4}
                borderTop="1px solid"
                borderColor={dividerColor}
            >
                <Text fontSize="10px" color={footerColor}>
                    Powered by{" "}
                    <Box
                        as="a"
                        href="https://www.vireliosoft.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        color={infoIconColor}
                        fontWeight="700"
                        style={{ textDecoration: "none" }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                    >
                        Vireliosoft
                    </Box>{" "}
                    /{" "}
                    <Box
                        as="a"
                        href="https://www.randevupp.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        color={infoIconColor}
                        fontWeight="700"
                        style={{ textDecoration: "none" }}
                        onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                        onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
                    >
                        RandevUpp
                    </Box>
                </Text>
                {FASTBOOKING_FOOTER && (
                    <Text fontSize="10px" color={footerColor} mt={1} style={{ opacity: 0.6 }}>
                        {FASTBOOKING_FOOTER}
                    </Text>
                )}
            </Box>
        </Box>
    )
}

const FastBookingWrapper = () => {
    return (
        /* Chakra v3 formatı: value={defaultSystem} */
        <ChakraProvider value={defaultSystem} resetCSS={false}>
            <FastBooking />
        </ChakraProvider>
    )
}

export default FastBookingWrapper;